import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { C, TIPOLOGIA_CONFIG } from '../../../../lib/design';
import { Ic, ICON_PATHS, GlobalStyles } from '../../../../lib/ui';
import {
  fetchFichaCaracterizacion,
  fetchProcesos,
  fetchPuestos,
} from '../../../shared/services/api';
import { useI18n } from '../../../../i18n';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import type { FichaData } from '../../../shared/services/api';
import { useActiveDocument } from '../../../../context/ActiveDocumentContext';
import { FONTS, DOC_COLORS } from '../../../../config/documentStyles';
import { useFichaEdit } from '../hooks/useFichaEdit';

import type {
  EntradaItem,
  SalidaItem,
  ReporteItem,
  RequisitoItem,
  HerramientaItem,
  InvolucradoItem,
  IndicadorItem,
} from '../components/ProcessSheetHelpers';
import {
  Section,
  Field,
  EmptyState,
  InlineEdit,
  InlineSelect,
  AddItemRow,
} from '../components/ProcessSheetHelpers';
import { ProcessSheetHeader } from '../components/ProcessSheetHeader';
import { ProcessSheetInputsOutputs } from '../components/ProcessSheetInputsOutputs';
import { ProcessSheetReportsReqs } from '../components/ProcessSheetReportsReqs';
import { ProcessSheetTools } from '../components/ProcessSheetTools';
import { ProcessSheetIndicators } from '../components/ProcessSheetIndicators';
import { ProcessSheetAiPanel } from '../components/ProcessSheetAiPanel';
import { DocLink } from '../components/ProcessSheetHelpers';

export function ProcessSheetPage() {
  const { t } = useI18n();
  const { codigo } = useParams<{ codigo: string }>();
  const navigate = useNavigate();
  const [ficha, setFicha] = useState<FichaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAi, setShowAi] = useState(false);
  const [aiMsg, setAiMsg] = useState('');
  const [zoom, setZoom] = useState(100);
  const [containerWidth, setContainerWidth] = useState(1200);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [editMode, setEditMode] = useState(false);

  const [procesosList, setProcesosList] = useState<
    { id: string; codigo: string; nombre: string }[]
  >([]);
  const [puestosList, setPuestosList] = useState<{ id: string; titulo: string }[]>([]);

  useEffect(() => {
    if (!editMode) return;
    fetchProcesos()
      .then((res) =>
        setProcesosList(res.data.map((p) => ({ id: p.id, codigo: p.codigo, nombre: p.nombre }))),
      )
      .catch(() => {});
    fetchPuestos()
      .then((res) =>
        setPuestosList(
          res.data.map((p: { id: string; titulo: string }) => ({ id: p.id, titulo: p.titulo })),
        ),
      )
      .catch(() => {});
  }, [editMode]);

  useEffect(() => {
    if (!codigo) return;
    setLoading(true);
    fetchFichaCaracterizacion(codigo)
      .then((res) => setFicha(res.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [codigo]);

  const { setActiveProceso, procesoCodigo: currentProcesoCodigo } = useActiveDocument();
  useEffect(() => {
    const c = ficha?.proceso?.codigo ?? codigo;
    if (c) {
      setActiveProceso(c, ficha?.proceso?.nombre ?? null);
    }
    return () => {
      if (currentProcesoCodigo === c) setActiveProceso(null);
    };
  }, [
    codigo,
    ficha?.proceso?.codigo,
    ficha?.proceso?.nombre,
    currentProcesoCodigo,
    setActiveProceso,
  ]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scaledDualWidth = (440 * 2 + 32) * (zoom / 100);
  const canDual = containerWidth >= scaledDualWidth;

  const procesoId = ficha?.proceso?.id ?? null;
  const entradas = useFichaEdit<EntradaItem>(procesoId, 'entradas');
  const salidas = useFichaEdit<SalidaItem>(procesoId, 'salidas');
  const reportes = useFichaEdit<ReporteItem>(procesoId, 'reportes');
  const requisitos = useFichaEdit<RequisitoItem>(procesoId, 'requisitos');
  const herramientas = useFichaEdit<HerramientaItem>(procesoId, 'herramientas');
  const involucradosHook = useFichaEdit<InvolucradoItem>(procesoId, 'involucrados');
  const indicadoresHook = useFichaEdit<IndicadorItem>(procesoId, 'indicadores');

  const [addingSection, setAddingSection] = useState<string | null>(null);
  const [newItemValue, setNewItemValue] = useState('');

  if (loading) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: FONTS.normative,
        }}
      >
        <LoadingSpinner text={t.process.cargandoFicha} size="lg" />
      </div>
    );
  }

  if (error || !ficha) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: FONTS.normative,
          color: '#EF4444',
        }}
      >
        {error ?? t.process.procesoNoEncontrado}
      </div>
    );
  }

  const { proceso, responsable, documentos, indicadores, rolesGobernanza } = ficha;

  const handleAdd = async (
    section: string,
    hook: { crear: (d: Record<string, unknown>) => Promise<unknown> },
  ) => {
    if (!newItemValue.trim()) return;
    const fieldMap: Record<string, Record<string, unknown>> = {
      entradas: { descripcion: newItemValue },
      salidas: { descripcion: newItemValue },
      reportes: { nombre: newItemValue },
      requisitos: { descripcion: newItemValue },
      herramientas: { nombre: newItemValue },
      involucrados: { rolDescripcion: newItemValue },
      indicadores: { nombre: newItemValue, tipoCalculo: 'MANUAL' },
    };
    await hook.crear(fieldMap[section] ?? { descripcion: newItemValue });
    setNewItemValue('');
    setAddingSection(null);
  };

  const fcDoc = documentos.find((d) => d.tipoDocumental === 'FC');
  const fichaCode = fcDoc?.codigo ?? `${proceso.codigo}.FC.01`;
  const tipoCfg = TIPOLOGIA_CONFIG[proceso.tipologia as keyof typeof TIPOLOGIA_CONFIG];
  const grupoFuncional = proceso.grupoNombre
    ? `Gr.${String(proceso.grupoNumero).padStart(2, '0')} ${proceso.grupoNombre}`
    : null;
  const docDocs = documentos.filter((d) => d.tipoDocumental !== 'HE');
  const toolDocs = documentos.filter((d) => d.tipoDocumental === 'HE');

  const responsableLabel = responsable
    ? `${responsable.puesto.titulo}${responsable.persona ? ` (${responsable.persona.nombre} ${responsable.persona.apellidos})` : ''}`
    : '--';

  const involucrados = rolesGobernanza.map((r) => {
    const label = r.nombrePersonalizado ?? r.puesto.titulo;
    return r.persona ? `${label} (${r.persona.nombre} ${r.persona.apellidos})` : label;
  });

  const kpis = [
    ...indicadores.slice(0, 2).map((ind) => ({
      name: ind.nombre.length > 20 ? ind.nombre.slice(0, 18) + '...' : ind.nombre,
      value: ind.valorObjetivo ?? 0,
      target: ind.unidad
        ? `Obj: ${ind.valorObjetivo ?? '--'} ${ind.unidad}`
        : `Obj: ${ind.valorObjetivo ?? '--'}`,
    })),
    {
      name: 'Documentos',
      value: documentos.length,
      target: `${documentos.filter((d) => d.estado === 'PUBLICADO').length} vigentes`,
    },
  ];

  function kpiColor(v: number) {
    if (v >= 80) return '#6EE7B7';
    if (v >= 50) return '#93C5FD';
    return '#FCD34D';
  }

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: FONTS.normative,
        background: C.bg,
      }}
    >
      <GlobalStyles />

      <ProcessSheetHeader
        fichaCode={fichaCode}
        procesoCodigo={proceso.codigo}
        procesoNombre={proceso.nombre}
        fcDocEstado={fcDoc ? fcDoc.estado : null}
        zoom={zoom}
        setZoom={setZoom}
        showAi={showAi}
        setShowAi={setShowAi}
        editMode={editMode}
        setEditMode={setEditMode}
      />

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <div ref={scrollRef} style={{ flex: 1, overflow: 'auto' }}>
          <div
            style={{
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              width: `${10000 / zoom}%`,
              marginLeft: `${(100 - 10000 / zoom) / 2}%`,
            }}
          >
            {/* Hero */}
            <div
              style={{
                background: `linear-gradient(135deg, ${C.azulMuyOscuro} 0%, ${DOC_COLORS.h1} 60%, ${DOC_COLORS.h2} 100%)`,
                padding: '24px 32px',
                position: 'relative',
                overflow: 'hidden',
                color: 'white',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: -40,
                  right: -40,
                  width: 160,
                  height: 160,
                  borderRadius: '50%',
                  background: 'rgba(51,188,225,0.05)',
                  pointerEvents: 'none',
                }}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 14,
                        fontWeight: 700,
                        background: 'rgba(255,255,255,0.12)',
                        padding: '3px 10px',
                        borderRadius: 5,
                      }}
                    >
                      {proceso.codigo}
                    </span>
                    <span
                      style={{
                        padding: '3px 10px',
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 600,
                        background: 'rgba(255,255,255,0.15)',
                      }}
                    >
                      {(t.typology as Record<string, string>)[proceso.tipologia] ?? tipoCfg.label}
                    </span>
                    {grupoFuncional && (
                      <span
                        style={{
                          padding: '3px 10px',
                          borderRadius: 6,
                          fontSize: 11,
                          fontWeight: 600,
                          background: 'rgba(255,255,255,0.1)',
                        }}
                      >
                        {grupoFuncional}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
                    {proceso.nombre}
                  </div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
                    Responsable: {responsableLabel}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, flexShrink: 0, marginLeft: 24 }}>
                  {kpis.map((k) => (
                    <div
                      key={k.name}
                      style={{
                        background: 'rgba(255,255,255,0.08)',
                        borderRadius: 8,
                        padding: '10px 16px',
                        minWidth: 140,
                        textAlign: 'center',
                      }}
                    >
                      <div
                        style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}
                      >
                        {k.name}
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: kpiColor(k.value) }}>
                        {k.value}
                        {typeof k.value === 'number' && k.name !== 'Documentos' ? '%' : ''}
                      </div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{k.target}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Content grid */}
            <div
              style={{
                padding: '24px 32px',
                maxWidth: canDual ? 1200 : 900,
                margin: '0 auto',
                display: 'grid',
                gridTemplateColumns: canDual ? '1fr 1fr' : '1fr',
                gap: '0 24px',
                alignItems: 'start',
              }}
            >
              {/* Section 1 - Identificacion */}
              <Section icon={<Ic d={ICON_PATHS.grid} s={16} />} title="1. Identificacion" fullWidth>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px 32px',
                  }}
                >
                  <Field label={t.processSheet.fields.code} value={proceso.codigo} />
                  <Field label={t.processSheet.fields.name} value={proceso.nombre} />
                  <Field
                    label={t.processSheet.fields.type}
                    value={
                      (t.typology as Record<string, string>)[proceso.tipologia] ?? tipoCfg.label
                    }
                  />
                  <Field label={t.processSheet.fields.group} value={grupoFuncional ?? '--'} />
                  <Field label={t.processSheet.fields.phase} value={proceso.fase ?? '--'} />
                  <Field label={t.processSheet.fields.responsible} value={responsableLabel} />
                </div>
                <div style={{ marginTop: 16 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#7a7a9a',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      marginBottom: 6,
                    }}
                  >
                    {t.processSheet.fields.involved}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {involucradosHook.items.length > 0 ? (
                      involucradosHook.items.map((inv) => (
                        <div
                          key={inv.id}
                          style={{
                            display: 'inline-flex',
                            flexDirection: 'column',
                            gap: 2,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              padding: '3px 10px',
                              borderRadius: 20,
                              background: `${DOC_COLORS.h2}08`,
                              color: DOC_COLORS.h2,
                              border: `1px solid ${DOC_COLORS.h2}15`,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <InlineEdit
                              value={inv.rolDescripcion ?? '--'}
                              onSave={(v) =>
                                involucradosHook.actualizar(inv.id, { rolDescripcion: v })
                              }
                              editMode={editMode}
                              style={{ fontSize: 12, color: DOC_COLORS.h2, flex: 'unset' }}
                            />
                            {editMode && (
                              <button
                                onClick={() => involucradosHook.eliminar(inv.id)}
                                style={{
                                  border: 'none',
                                  background: 'none',
                                  color: '#EF4444',
                                  cursor: 'pointer',
                                  fontSize: 12,
                                  padding: 0,
                                  lineHeight: 1,
                                }}
                              >
                                x
                              </button>
                            )}
                          </span>
                          {editMode && (
                            <InlineSelect
                              value={inv.puestoId ?? ''}
                              options={puestosList.map((p) => ({ value: p.id, label: p.titulo }))}
                              onSave={(v) =>
                                involucradosHook.actualizar(inv.id, { puestoId: v || null })
                              }
                              editMode={editMode}
                              style={{ fontSize: 10 }}
                            />
                          )}
                        </div>
                      ))
                    ) : involucrados.length > 0 ? (
                      involucrados.map((p, i) => (
                        <span
                          key={i}
                          style={{
                            fontSize: 12,
                            padding: '3px 10px',
                            borderRadius: 20,
                            background: `${DOC_COLORS.h2}08`,
                            color: DOC_COLORS.h2,
                            border: `1px solid ${DOC_COLORS.h2}15`,
                          }}
                        >
                          {p}
                        </span>
                      ))
                    ) : (
                      <EmptyState message={t.processSheet.noData} />
                    )}
                    {editMode && (
                      <AddItemRow
                        sectionKey="involucrados"
                        addingSection={addingSection}
                        setAddingSection={setAddingSection}
                        newItemValue={newItemValue}
                        setNewItemValue={setNewItemValue}
                        onAdd={() => handleAdd('involucrados', involucradosHook)}
                        addLabel={t.processSheet.addItem}
                        variant="pill"
                      />
                    )}
                  </div>
                </div>
              </Section>

              {/* Section 2 - Descripcion */}
              <Section icon={<Ic d={ICON_PATHS.doc} s={16} />} title="2. Descripcion">
                <p
                  style={{
                    fontSize: 14,
                    lineHeight: 1.7,
                    textAlign: 'justify',
                    margin: 0,
                    color: C.text,
                  }}
                >
                  {proceso.descripcion || <EmptyState message={t.process.sinDescripcion} />}
                </p>
                <div style={{ marginTop: 16 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#7a7a9a',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      marginBottom: 4,
                    }}
                  >
                    {t.process.alcance}
                  </div>
                  <EmptyState message={t.process.alcanceNoDefinido} />
                </div>
              </Section>

              {/* Section 3 - Inputs & Outputs */}
              <ProcessSheetInputsOutputs
                entradas={entradas}
                salidas={salidas}
                procesosList={procesosList}
                editMode={editMode}
                addingSection={addingSection}
                setAddingSection={setAddingSection}
                newItemValue={newItemValue}
                setNewItemValue={setNewItemValue}
                onAdd={handleAdd}
              />

              {/* Section 4 - Reports & Requirements */}
              <ProcessSheetReportsReqs
                reportes={reportes}
                requisitos={requisitos}
                editMode={editMode}
                addingSection={addingSection}
                setAddingSection={setAddingSection}
                newItemValue={newItemValue}
                setNewItemValue={setNewItemValue}
                onAdd={handleAdd}
              />

              {/* Section 5 - Documentacion */}
              <Section icon={<Ic d={ICON_PATHS.doc} s={16} />} title="5. Documentacion">
                {docDocs.length === 0 ? (
                  <EmptyState message={t.process.sinDocumentos} />
                ) : (
                  docDocs.map((d) => <DocLink key={d.id} doc={d} navigate={navigate} />)
                )}
              </Section>

              {/* Section 6 - Tools */}
              <ProcessSheetTools
                herramientas={herramientas}
                toolDocs={toolDocs}
                navigate={navigate}
                editMode={editMode}
                addingSection={addingSection}
                setAddingSection={setAddingSection}
                newItemValue={newItemValue}
                setNewItemValue={setNewItemValue}
                onAdd={handleAdd}
              />

              {/* Section 7 - Indicators */}
              <ProcessSheetIndicators
                indicadoresHook={indicadoresHook}
                indicadoresReadonly={indicadores}
                editMode={editMode}
                addingSection={addingSection}
                setAddingSection={setAddingSection}
                newItemValue={newItemValue}
                setNewItemValue={setNewItemValue}
                onAdd={handleAdd}
              />

              <div style={{ height: 32, gridColumn: '1 / -1' }} />
            </div>
          </div>
        </div>

        <ProcessSheetAiPanel
          showAi={showAi}
          aiMsg={aiMsg}
          setAiMsg={setAiMsg}
          onClose={() => setShowAi(false)}
        />
      </div>
    </div>
  );
}
